import { Injectable } from '@angular/core';
import { MessageService } from 'primeng/api';
import { TranslocoService } from '@jsverse/transloco';

// Default display duration for toasts (ms)
const GLOBAL_TOAST_DEFAULT_LIFE = 3000;

export interface GlobalToastMessage {
    severity?: 'info' | 'success' | 'warn' | 'error' | 'secondary' | 'contrast'; // Default is 'info'
    summary?: string; // Header text
    detail?: string; // Body text
    id?: string | number; // Custom identifier
    key?: string; // Target a specific <p-toast key="...">
    life?: number; // Display duration (ms) - overrides component default
    sticky?: boolean; // If true, requires manual dismiss
    closable?: boolean; // Force show/hide close icon
    data?: any; // payload for custom templates
    icon?: string; // Custom icon class
    closeIcon?: string; // Custom icon for close button
    styleClass?: string; // Additional css class for the toast container
    contentStyleClass?: string; // Class for inner content
}

@Injectable({ providedIn: 'root' })
export class GlobalToastMessageService {
    constructor (
        private readonly messageService: MessageService,
        private readonly translocoService: TranslocoService,
    ) {}

    private add (message: GlobalToastMessage): void {
        this.messageService.add({ life: GLOBAL_TOAST_DEFAULT_LIFE, ...message });
    }

    success (detail: string, summary?: string): void {
        const translated = this.translocoService.translate('toast.summary.success');
        const finalSummary = summary ?? (translated ?? '');
        this.add({ severity: 'success', summary: finalSummary, detail });
    }

    info (detail: string, summary?: string): void {
        const translated = this.translocoService.translate('toast.summary.info');
        const finalSummary = summary ?? (translated ?? '');
        this.add({ severity: 'info', summary: finalSummary, detail });
    }

    warn (detail: string, summary?: string): void {
        const translated = this.translocoService.translate('toast.summary.warning');
        const finalSummary = summary ?? (translated ?? '');
        this.add({ severity: 'warn', summary: finalSummary, detail });
    }

    error (detail: string, summary?: string): void {
        const translated = this.translocoService.translate('toast.summary.error');
        const finalSummary = summary ?? (translated ?? '');
        this.add({ severity: 'error', summary: finalSummary, detail });
    }

    secondary (detail: string, summary?: string): void {
        const translated = this.translocoService.translate('toast.summary.notice');
        const finalSummary = summary ?? (translated ?? '');
        this.add({ severity: 'secondary', summary: finalSummary, detail });
    }

    contrast (detail: string, summary?: string): void {
        const translated = this.translocoService.translate('toast.summary.notice');
        const finalSummary = summary ?? (translated ?? '');
        this.add({ severity: 'contrast', summary: finalSummary, detail });
    }

    // custom toast method that accepts a full GlobalToastMessage object
    message (message: GlobalToastMessage): void {
        this.add(message);
    }
}
